/**
 * Minimal WebZjs Keys-and-Send Client
 * 
 * This is a minimal wrapper around the new webzjs-keys-and-send package
 * which only exports key derivation and PCZT signing functions.
 * 
 * For transaction building, we now use the server-side PCZT builder API.
 */

import { mnemonicToSeedSync } from '@scure/bip39';
import wasmInit, {
	UnifiedSpendingKey,
	UnifiedFullViewingKey,
	derive_usk_from_mnemonic,
	ufvk_from_usk,
	pczt_sign,
	Pczt,
	generate_seed_phrase,
	SeedFingerprint,
	decode_unified_address_receivers,
	search_derivation_for_address,
} from './webzjs_keys_and_send_fixed';
import { wasmUrl } from '../../../../utils/wasmVersion';

// WASM initialization state
let wasmInitialized = false;
let initializationPromise: Promise<boolean> | null = null;

/**
 * Initialize the WebZjs WASM module
 */
async function initializeWasm(): Promise<boolean> {
	if (wasmInitialized) {
		return true;
	}

	if (initializationPromise) {
		return initializationPromise;
	}

	initializationPromise = (async () => {
		try {
			console.log('🔧 Initializing WebZjs Keys-and-Send WASM from /webzjs_keys_and_send_bg.wasm...');

			// The WASM filename never changes, and nginx serves it without a
			// Cache-Control header, so browsers heuristically cache it for days.
			// A network upgrade makes a stale copy fatal (the PCZT signer
			// rejects the new consensus branch id).
			//
			// The `?v=` used to be a marker bumped by hand — and on 29 Jul 2026
			// the binary was rebuilt for Ironwood while the marker stayed at
			// 'nu63', so every browser that had already fetched it kept the
			// pre-Ironwood signer. It is now the file's content hash, injected
			// at build time: rebuild the binary and the URL changes with it.
			const wasmPath = wasmUrl('webzjs_keys_and_send_bg.wasm');
			await wasmInit(wasmPath);
			
			wasmInitialized = true;
			console.log('✅ WebZjs Keys-and-Send WASM initialized successfully');
			return true;
		} catch (error) {
			console.error('❌ WebZjs Keys-and-Send WASM initialization failed:', error);
			wasmInitialized = false;
			throw error;
		}
	})();

	return initializationPromise;
}

/**
 * Minimal WebZjs client for key derivation and PCZT signing
 */
export class WebZjsKeysAndSendClient {
	private network: string;
	private lightwalletdUrl: string;

	constructor(network: string = 'main', lightwalletdUrl: string = 'https://lightwalletd.zcash.winbit32.com') {
		this.network = network;
		this.lightwalletdUrl = lightwalletdUrl;
	}

	/**
	 * Initialize the client
	 */
	async initialize(): Promise<void> {
		await initializeWasm();
		console.log(`✅ WebZjs client initialized for network: ${this.network}`);
	}

	/**
	 * Generate a unified address from mnemonic
	 */
	async generateUnifiedAddress(mnemonic: string, accountIndex: number = 0, receiverTypes?: { orchard?: boolean; sapling?: boolean; p2pkh?: boolean }): Promise<{
		address: string;
		usk: UnifiedSpendingKey;
		ufvk: string;
		seedFp: SeedFingerprint;
	}> {
		await this.initialize();

		console.log(`🔑 Deriving unified address for account ${accountIndex}...`);
		
		const seed = new Uint8Array(mnemonicToSeedSync(mnemonic));
		const seedFp = new SeedFingerprint(seed);
		const usk = derive_usk_from_mnemonic(this.network, mnemonic, accountIndex);
		const ufvkEncoded = ufvk_from_usk(usk, this.network);
		const ufvkObj = usk.to_unified_full_viewing_key();

		let address: string;
		if (receiverTypes) {
			const hasOrchard = receiverTypes.orchard !== false;
			const hasSapling = receiverTypes.sapling !== false;
			const hasP2pkh = receiverTypes.p2pkh === true;
			address = ufvkObj.to_unified_address_with_receivers(this.network, 0, hasOrchard, hasSapling, hasP2pkh);
		} else {
			address = ufvkObj.to_unified_address(this.network, 0);
		}
		
		console.log(`✅ Unified address generated: ${address.substring(0, 20)}...`);
		
		return { address, usk, ufvk: ufvkEncoded, seedFp };
	}

	/**
	 * Generate a unified address from raw seed bytes (non-mnemonic path).
	 * Used when deriving Zcash keys from a wallet signature.
	 */
	async generateUnifiedAddressFromSeed(seedBytes: Uint8Array, accountIndex: number = 0, receiverTypes?: { orchard?: boolean; sapling?: boolean; p2pkh?: boolean }): Promise<{
		address: string;
		usk: UnifiedSpendingKey;
		ufvk: string;
		seedFp: SeedFingerprint;
	}> {
		await this.initialize();

		if (!seedBytes || seedBytes.length < 32) {
			throw new Error('Seed must be at least 32 bytes');
		}

		const seedFp = new SeedFingerprint(seedBytes);
		const usk = new UnifiedSpendingKey(this.network, seedBytes, accountIndex);
		const ufvkEncoded = ufvk_from_usk(usk, this.network);
		const ufvkObj = usk.to_unified_full_viewing_key();

		let address: string;
		if (receiverTypes) {
			const hasOrchard = receiverTypes.orchard !== false;
			const hasSapling = receiverTypes.sapling !== false;
			const hasP2pkh = receiverTypes.p2pkh === true;
			address = ufvkObj.to_unified_address_with_receivers(this.network, 0, hasOrchard, hasSapling, hasP2pkh);
		} else {
			address = ufvkObj.to_unified_address(this.network, 0);
		}

		return { address, usk, ufvk: ufvkEncoded, seedFp };
	}

	/**
	 * Decode a unified address to discover which receiver types it contains
	 */
	async decodeUnifiedAddressReceivers(address: string): Promise<{
		hasOrchard: boolean;
		hasSapling: boolean;
		hasP2pkh: boolean;
		receiverCount: number;
	}> {
		await this.initialize();
		const json = decode_unified_address_receivers(address);
		return JSON.parse(json);
	}

	/**
	 * Search for derivation parameters that produce a given unified address.
	 * Tries account indices 0..maxAccount, diversifier indices 0..maxDiversifier,
	 * and all valid receiver type combinations.
	 */
	async searchDerivationForAddress(
		mnemonic: string,
		targetAddress: string,
		maxAccount: number = 5,
		maxDiversifier: number = 20,
	): Promise<{
		found: boolean;
		accountIndex?: number;
		diversifierIndex?: number;
		hasOrchard?: boolean;
		hasSapling?: boolean;
		hasP2pkh?: boolean;
	}> {
		await this.initialize();
		const json = search_derivation_for_address(
			this.network,
			mnemonic,
			targetAddress,
			maxAccount,
			maxDiversifier,
		);
		return JSON.parse(json);
	}

	/**
	 * Derive the default unified address from an *encoded* UFVK (`uview1…`).
	 * View-only: no spending capability is created. Used by the NFPT viewer's
	 * "view with a UFVK" option — the key never leaves the browser.
	 */
	async deriveAddressFromUfvk(ufvkEncoded: string, diversifierIndex: number = 0): Promise<string> {
		await this.initialize();

		const trimmed = (ufvkEncoded || '').trim();
		if (!trimmed) {
			throw new Error('Enter a unified full viewing key (uview1…)');
		}
		const ufvk = new UnifiedFullViewingKey(this.network, trimmed);
		try {
			return ufvk.to_unified_address(this.network, diversifierIndex);
		} finally {
			ufvk.free();
		}
	}

	/**
	 * Export Unified Full Viewing Key (UFVK)
	 */
	async exportUnifiedFullViewingKey(mnemonic: string, params?: { account?: number }): Promise<string> {
		await this.initialize();

		const accountIndex = params?.account ?? 0;
		const usk = derive_usk_from_mnemonic(this.network, mnemonic, accountIndex);
		
		// ufvk_from_usk now returns the encoded string directly
		return ufvk_from_usk(usk, this.network);
	}

	/**
	 * Sign a PCZT
	 */
	async signPczt(pczt: Pczt, usk: UnifiedSpendingKey): Promise<Pczt> {
		await this.initialize();
		
		const seedFp = usk.seed_fingerprint();
		return await pczt_sign(this.network, pczt, usk, seedFp);
	}

	/**
	 * Generate a new seed phrase
	 */
	static generateSeedPhrase(): string {
		return generate_seed_phrase();
	}

	/**
	 * Validate a Zcash address (stub for compatibility)
	 * For full validation, use validateZcashAddress from zcash-extension.ts
	 */
	async validateAddress(address: string): Promise<{ isValid: boolean; type: string }> {
		// Simple validation based on address prefix
		if (address.startsWith('u1')) {
			return { isValid: true, type: 'unified' };
		} else if (address.startsWith('zs1')) {
			return { isValid: true, type: 'sapling' };
		} else if (address.startsWith('t1') || address.startsWith('t3')) {
			return { isValid: true, type: 'transparent' };
		} else if (address.startsWith('ztestsapling')) {
			return { isValid: true, type: 'sapling' };
		}
		
		return { isValid: false, type: 'unknown' };
	}

	/**
	 * Stub methods for compatibility with old client
	 * These are no longer used in the PCZT architecture
	 */
	async getBalance(_address: string): Promise<{ balanceZec: number }> {
		console.warn('⚠️ getBalance is deprecated - use lightwalletd or RPC directly');
		return { balanceZec: 0 };
	}

	async sendTransaction(_params: any): Promise<any> {
		throw new Error('sendTransaction is deprecated - use PCZT builder API instead');
	}
}

/**
 * Singleton client instance cache
 */
const clientCache = new Map<string, WebZjsKeysAndSendClient>();

/**
 * Get or create a WebZjs client instance
 */
export function getWebZjsClient(network: string = 'main', lightwalletdUrl?: string): WebZjsKeysAndSendClient {
	const cacheKey = `${network}:${lightwalletdUrl || 'default'}`;
	
	if (!clientCache.has(cacheKey)) {
		clientCache.set(cacheKey, new WebZjsKeysAndSendClient(network, lightwalletdUrl));
	}
	
	return clientCache.get(cacheKey)!;
}

export const initializeWebZjs = async (): Promise<void> => {
	await initializeWasm();
};

export const isWebZjsInitialized = (): boolean => wasmInitialized;

export const generateWebZjsSeedPhrase = (): string => generate_seed_phrase();

// Re-export types and functions for direct use
export {
	UnifiedSpendingKey,
	derive_usk_from_mnemonic,
	ufvk_from_usk,
	pczt_sign,
	Pczt,
	generate_seed_phrase,
	decode_unified_address_receivers,
	search_derivation_for_address,
};

